<template>
	<div class="waiting-room-container">
		<div class="waiting-room">
			<router-link to="/rooms">
				<button class="btn btn-textual back-btn">
					<fa-icon icon="arrow-left" /> Back
				</button>
			</router-link>
			<div class="controls-group">
				<span class="direct-messages-position">
					<mini-direct-messages />
				</span>
				<span class="sound-control-position" v-if="!isSpectator">
					<sound />
				</span>
			</div>
			<div class="room-name">
				{{ roomName }}
			</div>
			<div class="content">
				<div class="players" v-if="!shouldJoin">
					<div class="player" v-for="player in players" :key="`player-${player.id}`">
						<alt-player-label
							:player="player"
							:renderMenu="shouldRenderPlayerMenu(player.user.id)"
							:theme="settings.theme"
						/>
					</div>
				</div>
				<form class="join-game" v-if="shouldJoin" v-on:submit.prevent="onJoinGame(gameId, password)">
					<div class="form-control-wrapper password-input-wrapper" v-if="passwordRequired">
						<input
							class="form-control outline password-input"
							v-model="password"
							name="room-password"
							type="password"
							placeholder="Enter room password"
						/>
						<fa-icon class="icon" icon="lock" />
					</div>
					<input class="btn btn-primary btn-block" type="submit" value="Join Game" />
				</form>
				<div class="teams" v-if="!shouldJoin">
					<div class="team" v-for="team in teams" :key="`team-${team.id}`">
						<royal-coin :color="team.color" :theme="settings.theme" />
						<button class= "btn btn-outline btn-3d" @click="onChooseTeam(team)" v-if="!teamIsFull(team.id)">
							Choose Team
						</button>
						<button class= "btn btn-outline btn-disabled" v-if="teamIsFull(team.id)">
							Team is Full
						</button>
					</div>
				</div>
				<div class="settings" v-if="!shouldJoin && isCreator"
					:class="{['expanded']: settingsExpanded}"
				>
					<div
						class="settings-title"
						@click="onToggleSettings"
					>
						<fa-icon icon="chevron-right" class="settings-toggle"  v-if="!settingsExpanded"/>
						<fa-icon icon="chevron-down" class="settings-toggle"  v-if="settingsExpanded"/>
						<span class="title-text">Advanced Settings</span>
					</div>
					<div class="settings-content" v-if="settingsExpanded">
						<div class="settings-options">
							<div class="settings-options-group">
								<span class="settings-option btn btn-3d"
										:class="{['selected']: settings.draftType === 'random'}"
										@click="setDraftType('random')"
									>
									Random Units
								</span>
								<span class="settings-option btn btn-3d"
									:class="{['selected']: settings.draftType === 'draft'}"
									@click="setDraftType('draft')"
								>
									Draft Units
								</span>
								<span class="settings-option btn btn-3d"
									:class="{['selected']:  settings.draftType === 'elimination_draft'}"
									@click="setDraftType('elimination_draft')"
								>
									Elimination Draft
								</span>
							</div>
						</div>
						<div class="settings-options">
							<div class="settings-options-group">
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']:
										!settings.nobilityExpansion &&
										!settings.siegeExpansion &&
										!settings.nightfallExpansion &&
										!settings.highSeasExpansion &&
										!settings.masteryExpansion &&
										!settings.shockExpansion &&
										!settings.championsExpansion
									}"
									@click="setNobilityExpansion(false);
										setSiegeExpansion(false);
										setHighSeasExpansion(false);
										setNightfallExpansion(false);
										setMasteryExpansion(false);
										setShockExpansion(false);
										setChampionsExpansion(false);
									"
								>
									Base game
								</span>
								<span
									class="settings-option btn btn-3d"
									:class="{
										['selected']: settings.nobilityExpansion,
									}"
									@click="setNobilityExpansion(!settings.nobilityExpansion)"
								>
									Nobility Expansion
								</span>
								<span
									class="settings-option btn btn-3d"
									:class="{
										['selected']: settings.siegeExpansion,
									}"
									@click="setSiegeExpansion(!settings.siegeExpansion)"
								>
									Siege Expansion
								</span>
							</div>
						</div>
						<div class="settings-options">
							<div class="settings-options-group">
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']: settings.nightfallExpansion}"
									@click="setNightfallExpansion(!settings.nightfallExpansion)"
								>
									Nightfall Expansion
								</span>
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']: settings.shockExpansion}"
									@click="setShockExpansion(!settings.shockExpansion)"
								>
									Shock Tactics Expansion
								</span>
							</div>
						</div>
						<div class="settings-options">
							<div class="settings-options-group">
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']: settings.championsExpansion}"
									@click="setChampionsExpansion(!settings.championsExpansion)"
								>
									Champions Expansion
								</span>
							</div>
						</div>
						<div class="settings-options">
							<div class="settings-options-group units-mode">
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']: settings.masteryExpansion}"
									@click="setMasteryExpansion(!settings.masteryExpansion)"
								>
									Mastery Expansion (unofficial)
								</span>
							</div>
						</div>
						<div class="settings-options">
							<div class="settings-options-group units-mode">
								<span
									class="settings-option btn btn-3d"
									:class="{['selected']: settings.highSeasExpansion}"
									@click="setHighSeasExpansion(!settings.highSeasExpansion)"
								>
									High Seas Expansion (unofficial)
								</span>
							</div>
						</div>
						<alt-units-selector
							:onToggleAltUnitsModal="onToggleAltUnitsModal"
							:onToggleAltUnitType="onToggleAltUnitType"
							:settings="settings"
						/>
						<div class="settings-options custom-unit-types-wrapper">
							<div class="settings-options-group">
								<span class="settings-option btn btn-3d"
										:class="{['selected']: settings.customUnits}"
										@click="toggleCustomUnits()"
									>
									Custom units
								</span>
							</div>
							<div class="settings-options-group custom-unit-types" v-if="settings.customUnits">
								<div class="custom-unit-types-controls">
									Select:
									<span class="unit-type-control" @click="selectAllCustomUnitTypes"> all</span> /
									<span class="unit-type-control" @click="deselectAllCustomUnitTypes"> none</span>
								</div>
								<unit-types-selector
									:unitTypes="unitTypes"
									:selectedUnitTypes="customUnitTypes"
									:nobilityExpansion="settings.nobilityExpansion"
									:siegeExpansion="settings.siegeExpansion"
									:onChange="onChangeCustomUnitTypes"
									:showRemarks="true"
								/>
							</div>
							<div class="settings-explanation readonly" v-if="settings.customUnits">
									<div>
										<div>
											<fa-icon icon="asterisk" class="icon" />Nobility Expansion only
										</div>
										<div>
											<icon icon="fort" class="icon fort-icon" />Siege Expansion only
										</div>
										<div>
											<fa-icon icon="feather" class="icon" />Mastery Expansion (unofficial)
										</div>
									</div>
									<div>
										<div
											class="remark"
											:class="{
												['valid']: customUnitTypes.length >= minUnitTypesRequired,
											}"
										>
											{{customUnitTypes.length}}/{{unitTypes.length}}
										</div>
									</div>
								</div>
							</div>
						<div class="settings-options" v-if="numOfPlayers === 2 && settings.draftType !== 'random'">
							<div class="settings-options-group units-mode">
								<span class="settings-option btn btn-3d"
										:class="{['selected']: settings.choosePlayOrder}"
										@click="setChoosePlayOrder(!settings.choosePlayOrder)"
									>
									Choose play order
								</span>
							</div>
							<div class="settings-explanation" @click="onToggleChoosePlayOrderModal">
								What's this?
							</div>
						</div>
						<div class="settings-options" v-if="numOfPlayers === 2">
							<div class="settings-options-group units-mode">
								<div class="settings-option btn btn-3d"
										:class="{['selected']: settings.twoPlayersFullBoard}"
										@click="setTwoPlayersFullBoard(!settings.twoPlayersFullBoard)"
									>
									Jackson Variant
									<div class="settings-option-caption">
										(Full board, 5 units each, first to 8 bases wins)
									</div>
								</div>
							</div>
						</div>
						<div class="settings-options settings-theme">
							<div class="theme-label">
								Theme
							</div>
							<div class="settings-options-group">
								<div class="settings-option btn btn-3d"
									:class="{['selected']: !settings.theme}"
									@click="setTheme(null)"
								>
									Default
								</div>
								<div class="settings-option btn btn-3d"
									:class="{['selected']: settings.theme}"
									@click="setTheme(THEME_TOURNAMENT_KIT)"
								>
									Tournament
								</div>
							</div>
						</div>
					</div>
				</div>
				<settings-summary
					v-if="!shouldJoin && !isCreator"
					:smallerText="smallerSettingsText"
					:gameState="gameState"
				/>
				<div class="start-game" v-if="!shouldJoin">
					<button
						class="btn btn-primary btn-3d"
						:class="{
							['btn-disabled']: submitting,
							['btn-muted']: submitting,
						}"
						@click="onStartGame"
						v-if="status === 'ready_to_start' && isCreator"
						:disabled="submitting"
					>
						Start Game
					</button>
					<button class="btn btn-primary btn-disabled" v-if="status === 'ready_to_start' && !isCreator">
						{{ statusLabel(status) }}
					</button>
					<button class="btn btn-primary btn-disabled" v-if="status !== 'ready_to_start'">
						{{ statusLabel(status) }}
					</button>
				</div>
				<!--
				<div class="play-vs-ai" v-if="isCreator && gameState.state === IGameStatus.WAITING_FOR_PLAYERS">
					<button class="btn btn-3d btn-monochrome btn-xsmall" @click="onPlayVsAi">
						<span v-if="queuedGames.length">
							Continue game vs AI
						</span>
						<span v-else>
							Play vs AI While You Wait
						</span>
					</button>
				</div> -->
				<div class="leave-game-btn-wrapper">
					<button class="btn btn-muted btn-leave-game btn-3d" @click="onLeaveGame">
						Leave game
					</button>
				</div>
			</div>
			<div class="overlay" v-if="showRoyalGuardModal || showChoosePlayOrderModal" @click="onCloseModals"></div>
			<div class="modal modal-royal-guard" v-if="showRoyalGuardModal">
				<span class="close-modal-btn" @click="onToggleRoyalGuardModal">
					<fa-icon class="close-modal-icon" icon="times" />
				</span>
				<div class="unit-card-wrapper">
					<div class="unit-card-title">
						Classic
					</div>
					<unit-card
						:unit-type="UNIT_TYPE_ROYAL_GUARD"
						:image-name="'royal_guard.png'"
						:quantity="5"
					/>
				</div>
				<div class="unit-card-wrapper">
					<div class="unit-card-title">
						Nobility Expansion
					</div>
					<unit-card
						:unit-type="UNIT_TYPE_ROYAL_GUARD_NOBILITY"
						:image-name="'royal_guard.png'"
						:quantity="5"
					/>
				</div>
			</div>
			<div class="modal modal-choose-play-order" v-if="showChoosePlayOrderModal">
				<span class="close-modal-btn" @click="onToggleChoosePlayOrderModal">
					<fa-icon class="close-modal-icon" icon="times" />
				</span>
				<div class="content">
					<div>
						When <strong>"Choose play order"</strong> is enabled, a player will
						be randomly selected to decide if they wish to <strong>draft</strong>
						first or second. The other player will then decide whether they wish
						to <strong>play</strong> first or second.
					</div>
					<div class="comment">
						*This option is only available for <strong>2-player</strong> games and only when
						<strong>Draft Units</strong> or <strong>Elimination Draft</strong> is enabled.
					</div>
				</div>
			</div>
			<alt-units-modal
				:show="showAltUnitsModal"
				:close="onToggleAltUnitsModal"
				:show-royal-guard="true"
			/>
		</div>
		<div class="room-carousel-title" v-if="!roomsLoading && rooms.length">
			 <img class="title-decoration" src="~@/assets/sword_left.png" />
			 Other Rooms Waiting For Players
			  <img class="title-decoration" src="~@/assets/sword_right.png" />
		</div>
		<div
			class="room-carousel"
			v-if="!roomsLoading && rooms.length"
		>
			<button
				class="carousel-btn prev"
				type="button"
				@click="scrollRooms(-1)"
				:disabled="carouselAtStart"
				aria-label="Scroll rooms left"
			>
				<fa-icon icon="chevron-left" />
			</button>
			<div class="room-carousel-window" ref="roomsCarousel">
				<div
					class="room-card"
					v-for="room in rooms"
					:key="`room-${room.id}`"
				>
					<room
						:room="room"
						:refreshTime="roomsRefreshTime"
						:condensed="true"
					/>
				</div>
			</div>
			<button
				class="carousel-btn next"
				type="button"
				@click="scrollRooms(1)"
				:disabled="carouselAtEnd"
				aria-label="Scroll rooms right"
			>
				<fa-icon icon="chevron-right" />
			</button>
		</div>
	</div>
</template>

<script>
import shuffle from 'lodash.shuffle';
import throttle from 'lodash.throttle';
import uniq from 'lodash.uniq';
import moment from 'moment';
import api from '../../helpers/api';
import {
    IDraftType,
    IGameStatus,
    THEME_TOURNAMENT_KIT,
} from '../Game/Game-types';
import {
    UNIT_TYPE_ROYAL_GUARD,
    UNIT_TYPE_ROYAL_GUARD_NOBILITY,
    UNIT_TYPE_EARL,
    UNIT_TYPE_HERALD,
    UNIT_TYPE_SAPPER,
    unitTypes,
	highSeasUnitTypes,
    masteryUnitTypes,
    shockUnitTypes,
    baseUnits,
    nobilityUnitTypes,
    siegeUnitTypes,
    nightfallUnitTypes,
	altUnitTypes,
} from '../Unit/Unit-types';
import { isMobileDevice } from '../../helpers/general';
import { MOBILE_MAX_WIDTH } from '../../App-types';

export default {
    name: 'WaitingRoom',
    created() {
        if (!this.settings.nobilityExpansion) {
            this.removeNobilityOnlyUnitTypes();
        }

        if (!this.settings.siegeExpansion) {
            this.removeSiegeOnlyUnitTypes();
        }

        if (this.settings.royalGuardType === UNIT_TYPE_ROYAL_GUARD) {
            this.selectRoyalGuard(UNIT_TYPE_ROYAL_GUARD_NOBILITY);
        }

        this.$store.dispatch('setCustomUnitTypes', this.gameState.customUnitTypes);
        this.initializeRoomList();
    },
    data() {
        return {
            password: '',
            submitting: false,
            ['UNIT_TYPE_ROYAL_GUARD']: UNIT_TYPE_ROYAL_GUARD,
            ['UNIT_TYPE_ROYAL_GUARD_NOBILITY']: UNIT_TYPE_ROYAL_GUARD_NOBILITY,
            ['THEME_TOURNAMENT_KIT']: THEME_TOURNAMENT_KIT,
            settingsExpanded: false,
            showAltUnitsModal: false,
            showRoyalGuardModal: false,
            showChoosePlayOrderModal: false,
            unitTypes,
            roomsLoading: true,
            roomsRefreshInterval: null,
            roomsRefreshTime: null,
            carouselAtStart: true,
            carouselAtEnd: false,
            carouselListenerAttached: false,
            carouselScrollLeft: 0,
			['IGameStatus']: IGameStatus,
			queuedGames: [],
        };
    },
    watch: {
        players(current, prev) {
            if (current.length > prev.length && this.soundReady) {
                this.$store.dispatch('playSound');
            }
        },
        'settings.nobilityExpansion': function validateNobilityUnitTypes(current, prev) {
            if (prev && !current) {
                this.removeNobilityOnlyUnitTypes();
            }
        },
        'settings.siegeExpansion': function validateSiegeUnitTypes(current, prev) {
            if (prev && !current) {
                this.removeSiegeOnlyUnitTypes();
            }
        },
    },
    beforeUnmount() {
        clearInterval(this.roomsRefreshInterval);
        const container = this.$refs.roomsCarousel;
        if (container) {
            container.removeEventListener('scroll', this.handleCarouselScroll);
        }
        this.carouselListenerAttached = false;
    },
    mounted() {
        this.$nextTick(() => {
            const container = this.$refs.roomsCarousel;

            if (container) {
                container.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
                this.updateCarouselControls();
                this.carouselListenerAttached = true;
            }

			this.getQueuedGames();
        });
    },
    computed: {
        gameState() {
            return this.$store.state.gameState;
        },
        soundState() {
            return this.$store.state.soundState;
        },
        player() {
            return this.$store.getters.player;
        },
        players() {
            return this.gameState.players;
        },
        teams() {
            return this.gameState.teams;
        },
        teamSize() {
            return this.gameState.numOfPlayers / 2;
        },
        roomName() {
            return this.gameState.roomName;
        },
        settings() {
            return this.$store.state.appState.gameSettings;
        },
        status() {
            return this.gameState.state;
        },
        shouldJoin() {
            return !this.players.filter(player => player.user.id === this.userId).length
                && this.gameState.state === IGameStatus.WAITING_FOR_PLAYERS;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        passwordRequired() {
            return this.gameState.passwordRequired;
        },
        isCreator() {
            return this.gameState.creatorId === this.userId;
        },
        soundReady() {
            return this.soundState.soundReady;
        },
        muted() {
            return this.$store.state.appState.muted;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        customUnitTypes() {
            return uniq(this.settings.customUnitTypes);
        },
        minUnitTypesRequired() {
            const { numOfPlayers } = this.gameState;

            let minimum = numOfPlayers === 2 ? 8 : 12;

            if (this.settings.draftType === IDraftType.ELIMINATION_DRAFT) {
                minimum += numOfPlayers;
            }

            if (this.settings.twoPlayersFullBoard) {
                minimum += 2;
            }

            return minimum;
        },
        smallerSettingsText() {
            return this.gameState.draftType === IDraftType.ELIMINATION_DRAFT &&
				this.gameState.choosePlayOrder;
        },
        numOfPlayers() {
            return this.gameState.numOfPlayers;
        },
        rooms() {
            const currentRoomId = this.gameId;
            const cutoff = moment().subtract(30, 'minutes');

            return this.$store.state.roomListState.rooms.filter((room) => {
                if (room.state !== IGameStatus.WAITING_FOR_PLAYERS ||
					room.id === currentRoomId ||
					room.settings?.aiMode
				) {
                    return false;
                }

                if (!room.createdAt) {
                    return true;
                }

                return moment(room.createdAt).isSameOrAfter(cutoff);
            });
        },
    },
    methods: {
        async initializeRoomList() {
            const interval = 60 * 1000;

            try {
                await this.$store.dispatch('getRooms');
            } catch (_error) {
                this.roomsLoading = false;
            }

            this.roomsLoading = false;
            this.roomsRefreshTime = moment();

            clearInterval(this.roomsRefreshInterval);

            this.roomsRefreshInterval = setInterval(() => {
                this.roomsRefreshTime = moment();
            }, interval);
            this.restoreCarouselScrollPosition();
        },
        scrollRooms(direction) {
            const container = this.$refs.roomsCarousel;
            if (!container) {
                return;
            }

            const scrollAmount = 400;

            container.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth',
            });
        },
        restoreCarouselScrollPosition() {
            this.$nextTick(() => {
                const container = this.$refs.roomsCarousel;
                if (container) {
                    if (!this.carouselListenerAttached) {
                        container.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
                        this.carouselListenerAttached = true;
                    }
                    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
                    const targetScrollLeft = Math.max(0, Math.min(this.carouselScrollLeft, maxScroll));
                    container.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
                    this.updateCarouselControls();
                } else {
                    this.carouselAtStart = true;
                    this.carouselAtEnd = true;
                    this.carouselListenerAttached = false;
                }
            });
        },
        handleCarouselScroll: throttle(function handleCarouselScroll() {
            this.updateCarouselControls();
        }, 100),
        updateCarouselControls() {
            const container = this.$refs.roomsCarousel;
            if (!container) {
                this.carouselAtStart = true;
                this.carouselAtEnd = true;
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = container;

            this.carouselScrollLeft = scrollLeft;
            this.carouselAtStart = scrollLeft <= 0;
            this.carouselAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        },
        async onChangeCustomUnitTypes(customUnitTypes) {
            const prevSettings = { ...this.settings };
            await this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
            this.updateGameSettings(prevSettings);
        },
        async deselectAllCustomUnitTypes() {
            const prevSettings = { ...this.settings };
            await this.$store.dispatch('setCustomUnitTypes', []);
            this.updateGameSettings(prevSettings);
        },
        async selectAllCustomUnitTypes() {
            const prevSettings = { ...this.settings };
            const unitTypesToSelect = unitTypes.filter((unitType) => {

                if (!this.settings.nobilityExpansion && [UNIT_TYPE_EARL, UNIT_TYPE_HERALD].includes(unitType)) {
                    return false;
                }

                if (!this.settings.siegeExpansion && [UNIT_TYPE_SAPPER].includes(unitType)) {
                    return false;
                }

                return true;
            });

            await this.$store.dispatch('setCustomUnitTypes', unitTypesToSelect);
            this.updateGameSettings(prevSettings);
        },
        async onChooseTeam(team) {
            const teamId = team.id;

            const playerId = this.player.id;

            const response = await api.request('/game/assignTeam', 'POST', {
                teamId,
                playerId,
            });

            if (response.ok) {
                this.$notify({
                    group: 'app',
                    title: 'Success!',
                    type: 'success',
                    text: 'Team successfully assigned',
                });

                this.$store.dispatch('setSoundReady');
            } else {
                const error = await response.json();

                this.$notify({
                    group: 'app',
                    clean: true,
                });

                this.$notify({
                    group: 'app',
                    title: 'Info',
                    type: 'info',
                    text: error.message,
                });
            }
        },
        async onStartGame() {
            if (this.submitting) {
                return;
            }

            if (this.settings.customUnits &&
                this.settings.customUnitTypes.length < this.minUnitTypesRequired) {
                await this.addMinimumRequiredUnitTypes();
            }

            this.submitting = true;

            const { gameId, settings } = this;

            const response = await api.request('/game/start', 'POST', { gameId, settings });

            if (response.ok) {
                this.$store.dispatch('setSoundReady');
            } else {
                const error = await response.json();
                const errorType = error.code === 400 ? 'info' : 'error';

                this.$notify({
                    group: 'app',
                    title: '',
                    type: errorType,
                    text: error.message,
                });
            }

            this.submitting = false;
        },
        async onLeaveGame() {
            this.$store.dispatch('leaveGame', this.gameId);
        },
        async onJoinGame(gameId, password) {
            if (this.passwordRequired && !password) {
                this.$notify({
                    group: 'app',
                    title: '',
                    type: 'info',
                    text: 'Password required',
                });
                return;
            }

            const options = {
                gameId,
                password,
            };

            this.$store.dispatch('joinRoom', options);
        },
	 	async onPlayVsAi() {
            const settings = {
				altFootman: false,
				altLancer: false,
				altMarshal: false,
				aiMode: true,
				queueMode: true,
				blitz: null,
				choosePlayOrder: false,
				customUnitTypes: unitTypes,
				customUnits: false,
				draftMode: false,
				draftType: 'random',
				highSeasExpansion: false,
				masteryExpansion: false,
				nobilityExpansion: false,
				ranked: true,
				royalGuardType: UNIT_TYPE_ROYAL_GUARD_NOBILITY,
				nightfallExpansion: false,
				siegeExpansion: false,
				shockExpansion: false,
				timeLimit: 5,
				twoPlayersFullBoard: false,
				altUnits: false,
				theme: null,
				allowedRanks: [],
				roomName: '',
            };

			if (this.queuedGames.length) {
				this.$router.push(`/game/${this.queuedGames[0]?.id}`);
			} else {
				this.$store.dispatch('createGame', settings);
			}
        },
        teamPlayers(teamId) {
            return this.players.filter(player => player.teamId === teamId);
        },
        teamIsFull(teamId) {
            return this.teamPlayers(teamId).length === this.teamSize;
        },
        statusLabel(status) {
            let label;

            switch (status) {
                case IGameStatus.WAITING_FOR_TEAM_SELECTION:
                    label = 'Please select a team';
                    break;
                case IGameStatus.READY_TO_START:
                    label = 'Waiting for game to start';
                    break;
                case IGameStatus.DRAFTING:
                    label = 'Drafting units';
                    break;
                case IGameStatus.ELIMINATION_DRAFTING:
                    label = 'Elimination drafting';
                    break;
                case IGameStatus.STARTED:
                    label = 'Game started';
                    break;
                case IGameStatus.ENDED:
                    label = 'Game ended';
                    break;
                case IGameStatus.CANCELLED:
                    label = 'Game cancelled';
                    break;
                default:
                    label = 'Waiting for players';
            }

            return label;
        },
        onToggleSettings() {
            this.settingsExpanded = !this.settingsExpanded;
        },
        selectRoyalGuard(type) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setRoyalGuardType', type);
            this.updateGameSettings(prevSettings);
        },
        onToggleAltUnitsModal(event) {
			event.preventDefault();
			event.stopPropagation();
            this.showAltUnitsModal = !this.showAltUnitsModal;
        },
        onToggleAltUnitType(type) {
            this.$store.dispatch('setAltUnitType', type);
            this.updateGameSettings();
        },
        onToggleRoyalGuardModal() {
            this.showRoyalGuardModal = !this.showRoyalGuardModal;
        },
        onToggleChoosePlayOrderModal() {
            this.showChoosePlayOrderModal = !this.showChoosePlayOrderModal;
        },
        setTheme(value) {
            this.$store.dispatch('setTheme', value);
            this.updateGameSettings();
        },
        onCloseModals() {
            this.showRoyalGuardModal = false;
            this.showChoosePlayOrderModal = false;
        },
        setChoosePlayOrder(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setChoosePlayOrder', value);
            this.updateGameSettings(prevSettings);
        },
        setDraftMode(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setDraftMode', value);
            this.updateGameSettings(prevSettings);
        },
        setDraftType(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setDraftType', value);
            this.updateGameSettings(prevSettings);
        },
		setChampionsExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setChampionsExpansion', value);
            this.updateGameSettings(prevSettings);
        },
		setHighSeasExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setHighSeasExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setMasteryExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setMasteryExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setNightfallExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setNightfallExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setNobilityExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setNobilityExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setSiegeExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setSiegeExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setShockExpansion(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setShockExpansion', value);
            this.updateGameSettings(prevSettings);
        },
        setTwoPlayersFullBoard(value) {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('setTwoPlayersFullBoard', value);
            this.updateGameSettings(prevSettings);
        },
        toggleCustomUnits() {
            const prevSettings = { ...this.settings };
            this.$store.dispatch('toggleCustomUnits', !this.settings.customUnits);
            this.updateGameSettings(prevSettings);
        },
        toggleSound() {
            this.$store.dispatch('setSoundReady');
            this.$store.dispatch('setMuted', !this.muted);
        },
        updateGameSettings(prevSettings) {
            this.$store.dispatch('updateGameSettings', {
                gameId: this.gameId,
                settings: this.settings,
                prevSettings,
            });
        },
        async addMinimumRequiredUnitTypes() {
            const isNobility = this.settings.nobilityExpansion;
            const isSiege = this.settings.siegeExpansion;
            const isNightfall = this.settings.nightfallExpansion;
            const isMastery = this.settings.masteryExpansion;
            const isHighSeas = this.settings.highSeasExpansion;
            const isShockExpansion = this.settings.shockExpansion;

            let customUnitTypes = this.customUnitTypes;

            const missingUnitTypeCount = this.minUnitTypesRequired - this.customUnitTypes.length;

            if (missingUnitTypeCount >= 0) {
                let availableUnitTypes = baseUnits;

                if (isNobility) {
                    availableUnitTypes = [...availableUnitTypes, ...nobilityUnitTypes];
                }

                if (isSiege) {
                    availableUnitTypes = [...availableUnitTypes, ...siegeUnitTypes];
                }

                if (isNightfall) {
                    availableUnitTypes = [...availableUnitTypes, ...nightfallUnitTypes];
                }

				if (isShockExpansion) {
                    availableUnitTypes = [...availableUnitTypes, ...shockUnitTypes];
                }

                if (isMastery) {
                    availableUnitTypes = [...availableUnitTypes, ...masteryUnitTypes];
                }

				if (isHighSeas) {
                    availableUnitTypes = [...availableUnitTypes, ...highSeasUnitTypes];
                }

				availableUnitTypes = availableUnitTypes.filter(unitType => !customUnitTypes.includes(unitType));

                availableUnitTypes = shuffle(availableUnitTypes);

                customUnitTypes = customUnitTypes.concat(availableUnitTypes.slice(0, missingUnitTypeCount));

                await this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
            }
        },
        removeNobilityOnlyUnitTypes() {
            const customUnitTypes = this.customUnitTypes.filter(unitType =>
                ![UNIT_TYPE_EARL, UNIT_TYPE_HERALD].includes(unitType),
            );
            this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
        },
        removeSiegeOnlyUnitTypes() {
            const customUnitTypes = this.customUnitTypes.filter(unitType =>
                unitType !== UNIT_TYPE_SAPPER,
            );
            this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
        },
        removeMasteryUnitTypes() {
            const customUnitTypes = this.customUnitTypes.filter(unitType =>
                !masteryUnitTypes.includes(unitType),
            );
            this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
        },
        removeShockUnitTypes() {
            const customUnitTypes = this.customUnitTypes.filter(unitType =>
                !shockUnitTypes.includes(unitType) && !altUnitTypes.includes(unitType)
            );

            this.$store.dispatch('setCustomUnitTypes', customUnitTypes);
			this.updateGameSettings(this.settings);
        },
        encodeLabel(label) {
            return encodeURIComponent(label);
        },
        shouldRenderPlayerMenu(userId) {
            return this.userId !== userId;
        },
		isMobile() {
            return window.innerWidth <= MOBILE_MAX_WIDTH || isMobileDevice();
        },
		async getQueuedGames() {
			const response = await api.request(`/user/queuedGames`);

			if (response.ok) {
				this.queuedGames = await response.json();
			}
		}
    },
	sockets: {
        connect() {
            setTimeout(() => {
                if (this.isMobile()) {
                    this.$store.dispatch('getRooms');
                }
            }, 0);
        },
        onRoomsUpdated(rooms) {
            const container = this.$refs.roomsCarousel;
            if (container) {
                this.carouselScrollLeft = container.scrollLeft;
            }
            this.$store.dispatch('setRooms', rooms);

			if (this.carouselScrollLeft > 0) {
           		this.restoreCarouselScrollPosition();
			}
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

	.waiting-room-container {
		width: 100%;

		.waiting-room {
			background: white;
			text-align: center;
			margin: 50px auto;
			border-radius: 4px;
			width: 375px;
			max-width: 97%;
			color: $gray;
			position: relative;

			.controls-group {
				display: flex;
				position: absolute;
				right: 0;
				top: -27px;
			}

			.back-btn {
				position: absolute;
				top: -27px;
				left: 0;
				font-weight: 300;

				.icon {
					font-weight: 300;
				}
			}

			.room-name {
				font-size: 1.2em;
				line-height: 1.3em;
				padding: 10px 0;
				background: $dark-brown;
				color: $light-gray;
			}

			.content {
				padding: 20px;

				.players {

					.player {
						display: flex;
						justify-content: center;

						> * {
							margin: 0 2.5px;
							align-self: center;
						}

						.link {
							color: $gray;
						}
					}

					.royal-coin {
						height: auto;
						max-height: 16px;
						max-width: 16px;
						width: auto;
						vertical-align: middle;
					}
				}

				.teams {
					display: flex;
					justify-content: center;
					margin: 20px auto;

					.team {
						display: flex;
						justify-content: center;
						margin: 0 10px;
						border-radius: 4px;
						flex-direction: column;
						border: 1px solid transparent;
						background: $extra-light-gray-2;
						padding: 18px 10px;

						.royal-coin {
							height: 100px;
							width: 100px;
							align-self: center;
						}

						&:hover {
							border: 1px solid #d3d3d3;
						}

						.btn {
							font-size: .8em;
							margin: 15px auto 0;

							&.btn-3d {
								box-shadow: 0 3px $light-gray-alt;

								&:hover {
									box-shadow: 0 1px $light-gray-alt;
								}
							}
						}
					}
				}

				.settings {
					margin: 0 28px 20px;
					font-size: .8em;
					color: $gray;
					text-align: left;
					background: $extra-light-gray-2;
					border-radius: 4px;
					padding: 8px 10px;

					&.expanded {
						border-radius: 4px;

						.settings-title {
							text-align: left;
							position: relative;
						}

						.close-settings-icon {
							position: absolute;
							right: 0;
						}
					}

					.settings-content {
						text-align: left;
						padding: 12px 0 4px;

						.settings-options {

							&:not(:last-of-type) {
								margin-bottom: 10px;
							}

							&.custom-unit-types-wrapper {

								.custom-unit-types {
									display: flex;
									flex-direction: column;
								}

								.custom-unit-types-controls {
									text-align: center;
									margin-bottom: 7.5px;
									font-size: .7rem;

									.unit-type-control {
										cursor: pointer;
										font-weight: bold;
									}
								}

								.settings-explanation {
									text-align: left;
									position: relative;

									.icon {
										font-size: .6em;
										vertical-align: top;
										margin-top: 2px;
										margin-right: 1px;
									}

									.fort-icon {
										fill: $gray;
										height: auto;
										max-height: 6px;
										max-width: 10px;
										width: auto;
									}

									.remark {
										bottom: 0;
										position: absolute;
										right: 0;

										&.valid {
											color: $archer;
										}

										&.invalid {
											color: $lancer;
										}
									}
								}
							}

							.settings-options-group {
								display: flex;

								&.alt-unit-types {
									display: flex;
									justify-content: space-evenly;
									margin-bottom: 18px;
								}

								.settings-option {
									background: white;
									border-radius: 4px;
									border: 1px solid $light-gray;
									color: $medium-gray;
									cursor: pointer;
									flex-grow: 1;
									font-size: .9em;
									font-weight: normal;
									margin: 0 4px;
									padding: 5px 10px;
									position: relative;
									text-align: center;
									user-select: none;

									.settings-option-caption {
										font-size: .65rem;
										margin-top: 5px;
									}

									&.selected {
										color: $dark-gray;
										border: 1px solid $gray;
										box-shadow: 0 2px $gray;

										.new-badge {
											opacity: 1;
										}

										&:hover {
											top: 0;
											box-shadow: 0 2px $gray;
										}
									}

									&.limitation {
										border: 1px solid $lancer;
										box-shadow: 0 2px $lancer;
										color: $lancer;

										&:hover {
											top: 0;
											box-shadow: 0 2px $lancer;
										}
									}

									&:first-of-type {
										margin-left: 0;
									}

									&:last-of-type {
										margin-right: 0;
									}

									.new-badge {
										background: $green;
										border-radius: 8px;
										color: white;
										font-size: .65rem;
										margin-left: 14px;
										opacity: .65;
										padding: 2px 4px;
										position: absolute;
										right: 7px;
										top: 50%;
										transform: translateY(-50%);
									}
								}
							}
						}

						.settings-explanation {
							margin-top: 8px;
							font-size: .8em;
							text-align: center;
							cursor: pointer;

							.more-info {
								color: $green;
								cursor: pointer;
							}

							&.alt-unit-types-title {
								margin-bottom: 8px;
							}

							&.readonly {
								cursor: default;
							}
						}

						.setting-label {
							margin-bottom: 5px;
						}
					}

					.settings-title {
						cursor: pointer;
						font-weight: normal;
						font-size: .9em;

						.title-text {
							margin-left: 2px;
						}
					}

					.expansion-limit-wrapper {
						border-radius: 4px;
						color: $lancer;
						display: flex;
						font-size: .7rem;

						.btn {
							align-self: center;
							padding: 4px 6px;
							white-space: nowrap;
						}
					}

					.btn {

						&.btn-3d {
							box-shadow: 0 2px $light-gray-alt;

							&:hover {
								top: 1px;
								box-shadow: 0 1px $light-gray-alt;
							}
						}
					}
				}

				.custom-unit-types {
					margin-top: 10px;
				}
			}

			.password-input-wrapper {
				position: relative;
				margin-top: 0;

				.password-input {
					padding-left: 31px;

					&::placeholder {
						color: $dark-blue;
					}
				}

				.icon {
					position: absolute;
					left: 10px;
					top: 50%;
					transform: translateY(-50%);
				}
			}

			.leave-game-btn-wrapper {
				position: absolute;
				bottom: -44px;
				margin: 0 auto;
				left: 50%;
				transform: translateX(-50%);

				.btn-leave-game {
					font-weight: 300;
					font-size: .85em;
					padding: 5px 7px;
					color: $gray;
				}
			}

			.modal {

				&.modal-royal-guard {
					display: flex;
					padding: 25px;

					.unit-card-wrapper {

						.unit-card-title {
							font-size: 1.1em;
							margin-bottom: 6px;
							line-height: 1.4em;
							font-weight: bold;
							font-family: 'IrishUncialfabeta', 'RobotoSlab', 'Arial';
						}

						&:not(:last-of-type) {
							margin-right: 25px;
						}
					}
				}

				&.modal-choose-play-order {
					max-width: 485px;
					padding: 10px;
					text-align: left;
				}

				.comment {
					margin-top: 25px;
					font-size: .8em;
				}

				.close-modal-btn {
					cursor: pointer;
					position: absolute;
					right: 15px;
					top: 15px;

					.close-modal-icon {
						font-size: 1.1em;
						color: $dark-brown;
					}
				}
			}

			.settings-theme {
				display: flex;
				align-items: center;
				justify-content: center;

				.theme-label {
					margin-right: 7.5px;
				}
			}

			.play-vs-ai {
				margin-top: 20px;
			}
		}

		.room-carousel-title {
			margin-top: 70px;
			font-weight: bold;
			display: flex;
			align-items: center;
			justify-content: center;

			.title-decoration {
				height: auto;
				max-height: 25px;
				max-width: 25px;
				width: auto;
				margin: 0 5px;
			}
		}

		.room-carousel {
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 10px auto;
			padding: 0 20px;
			gap: 15px;
			width: 650px;
			max-width: 97%;
		}

		.room-carousel-window {
			flex: 1;
			overflow-x: auto;
			display: flex;
			gap: 15px;
			align-items: stretch;
			scroll-behavior: smooth;
			padding: 10px 0;
			scroll-padding: 0 40px;
			-webkit-overflow-scrolling: touch;
			touch-action: pan-x;
		}

		.room-carousel-window::-webkit-scrollbar {
			display: none;
		}

		.room-card {
			display: flex;
			align-items: stretch;
		}

		.room-card :deep(.room) {
			height: 100%;
			display: flex;
		}

		.carousel-btn {
			background: rgba(255, 255, 255, 0.75);
			border: 1px solid rgba(86, 72, 68, 0.25);
			border-radius: 50%;
			color: $dark-brown;
			width: 38px;
			height: 38px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			transition: background-color .2s ease;

			&:hover:not(:disabled) {
				background: rgba(255, 255, 255, 0.9);
			}

			&:disabled {
				visibility: hidden;
				color: rgb(86, 72, 68, .25);

				cursor: default;
			}

			.fa-icon {
				color: inherit;
			}
		}

		.room-carousel-empty {
			text-align: center;
			margin: 60px auto;
			font-size: 1.05em;
			color: $dark-blue;
			padding: 0 20px;
		}

		.room-list-placeholder {
			text-align: center;
			margin: 60px auto;
			font-size: 1.1em;
			color: $dark-blue;
		}

		.btn-subtext {
			font-size: .6rem;
		}
	}
</style>
